import {
  Cross,
  TickCircle,
  Help,
  Warning,
  Error,
} from "@asphalt-react/iconpack";
import IconTuner from "@asphalt-react/icontuner";
import * as React from "react";
import { animated, useSpring } from "react-spring";
import styled, { DefaultTheme } from "styled-components";
import { IToastItem } from "./Toast.store";

interface ToastItemElProps {
  color: keyof DefaultTheme["colors"]["background"];
}

const ToastItemEl = styled(animated.div)<ToastItemElProps>`
  align-items: start;
  background-color: white;
  box-sizing: border-box;
  border-left: 3px solid
    ${(props) => props.theme.colors.background[props.color]};
  border-radius: 2px;
  box-shadow: 0 0 2px 0 rgba(67, 90, 111, 0.1),
    0 8px 10px 0 rgba(67, 90, 111, 0.3);
  display: flex;
  padding: 14px 16px;
  margin-bottom: 24px;
  width: 100%;
`;

const ToastTitleEl = styled.p`
  color: rgb(27, 40, 66);
  font-family: MaisonNeueDemi, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 8px;
`;

const ToastMessageEl = styled.p`
  color: rgb(27, 40, 66);
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  margin-bottom: 8px;
`;

const ToastContentEl = styled.div`
  flex: auto;
  margin-left: 12px;
`;

const ToastCloseEl = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  height: 24px;
  min-width: 16px;
  outline: none;
  padding: 0;
  width: 24px;
`;

interface Props {
  toastItem: IToastItem;
  onClose: (uuid: string) => void;
}

export const Toast = (props: Props) => {
  const { toastItem, onClose } = props;
  const { color = "primary" } = toastItem;
  const animatedProps = useSpring({
    opacity: 1,
    transform: "translate3d(0, 0px, 0)",
    from: {
      opacity: 0,
      transform: "translate3d(128px, 0, 0)",
    },
  });

  const renderIcon = () => {
    if (toastItem.color === "info") {
      return (
        <IconTuner info>
          <Help />
        </IconTuner>
      );
    }
    if (toastItem.color === "warning") {
      return (
        <IconTuner warning>
          <Warning />
        </IconTuner>
      );
    }
    if (toastItem.color === "danger") {
      return (
        <IconTuner danger>
          <Error />
        </IconTuner>
      );
    }
    return (
      <IconTuner success>
        <TickCircle />
      </IconTuner>
    );
  };

  return (
    <ToastItemEl className="asp-toast" color={color} style={animatedProps}>
      <div style={{ minWidth: "16px" }}>{renderIcon()}</div>
      <ToastContentEl>
        {toastItem.title && <ToastTitleEl>{toastItem.title}</ToastTitleEl>}
        {toastItem.message && (
          <ToastMessageEl>{toastItem.message}</ToastMessageEl>
        )}
      </ToastContentEl>
      <ToastCloseEl onClick={() => onClose(toastItem.uuid!)}>
        <Cross />
      </ToastCloseEl>
    </ToastItemEl>
  );
};
